<template>
  <b-container fluid>
    <template v-if="showSurveyModal">
      <survey-modal :show-modal="showModal" />
    </template>
    <template v-if="hasApiCallFailed">
      <something-went-wrong :text="$tc('licenses', 1)" />
    </template>
    <template v-if="loading">
      <loading />
    </template>
    <template v-else>
      <b-row>
        <search
          @findLicenseByChange="handleFindLicenseByChange"
          @searchInputChange="handleSearchInputChange"
          @orderDateChange="handleOrderDateChange"
          @licenseTypeChange="handleLicenseTypeChange"
          @fromDateChange="handleFromDateChange"
          @toDateChange="handleToDateChange"
          @advancedSearchButtonClick="handleAdvancedSearchButtonClick"
          @onSearchClick="handleSearchClick"
          v-bind="search"
          :isAdvancedSearchVisible="search.showAdvancedSearch"
        />
      </b-row>
      <b-row>
        <quick-find
          :hideQuickFind="areBothTablesEmpty"
          @filterClicked="handleQuickFindClick"
          page="Landing page"
        />
      </b-row>
      <b-row v-if="areBothTablesEmpty" style="margin: 10px 0 50px;">
        <b-col class="text-center">
          <b-button
            to="/licenses"
            variant="primary"
            size="lg"
            class="view-all-licenses-btn"
          >
            {{ $t('viewAllLicenses') }}
          </b-button>
        </b-col>
      </b-row>
      <license-table
        isTypeRecentPurchases
        :allItems="newPurchases"
        :total="newPurchasesTotal"
        :getUpdatedLicenses="getLicenses"
      />
      <b-row style="flex-direction: column;">
        <div class="links-container" v-if="!isInternal || (isInternal && isPersonInRole)">
          <a href="#" @click.stop.prevent="handleWebKeyAllocation" class="blue-link">
            {{ $t('dontSeeLicenses') }}
          </a>
        </div>
        <div class="links-container">
          <a href="#" @click="allocateManualSurvey" class="'margin-bottom: 10px', blue-link" v-b-modal="modalName">
            {{ $t('surveyLicense') }}
          </a>
        </div>
      </b-row>
      <license-table :allItems="expiringLicenses" :total="expiringLicensesTotal" />
    </template>
    <template v-if="isApiCallDone && newPurchases && expiringLicenses">
      <template v-if="areBothTablesEmpty">
        <no-license-activity mode="licenseActivity" />
      </template>
      <template v-else-if="newPurchases.length === 0">
        <no-license-activity mode="newPurchases" />
      </template>
      <template v-else-if="expiringLicenses.length === 0">
        <no-license-activity mode="expiringLicenses" />
      </template>
    </template>
    <template v-if="isApiCallDone && areBothTablesEmpty">
      <did-you-know />
    </template>
    <b-row style="flex-direction: column;">
      <div class="links-container" v-if="!loading">
        <a :href="renewLicensesUrl" target="_blank" class="blue-link">
          {{ $t('renewMyLicenses') }}
        </a>
        <span> | </span>
        <a :href="upgradeLicensesUrl" target="_blank" class="blue-link">
          {{ $t('upgradeMyLicenses') }}
        </a>
      </div>
      <div class="links-container" v-if="!loading">
        <a :href="hardwareUrl" target="_blank" v-if="showHardware" class="blue-link">
          {{ $t('viewPendingHardwareReturns') }}
        </a>
        <span v-if="showMetaframe && showHardware"> | </span>
        <a :href="metaframeUrl" target="_blank" v-if="showMetaframe" class="blue-link">
          {{ $t('activateCitrixMetaframeXPLicenses') }}
        </a>
      </div>
    </b-row>
  </b-container>
</template>

<script>
  import Vue from 'vue'
  import { mapGetters, mapMutations } from 'vuex'

  import LicenseTable from './LicenseTable'
  import DidYouKnow from './DidYouKnow'
  import SomethingWentWrong from './SomethingWentWrong'
  import NoLicenseActivity from './NoLicenseActivity'
  import Loading from './Loading'
  import SurveyModal from './SurveyModal'
  import SearchMixin from '../mixins/SearchMixin'

  const requestBody = {
    advDateFrom: null,
    advDateTo: null,
    advSearchBy: null,
    dateFrom: null,
    dateTo: null,
    duration: 0,
    noOfItems: 25,
    pageIndex: 0,
    pageNumber: 1,
    quickFind: 'active',
    searchBy: null,
    searchByValue: null,
    sortBy: null,
    sortByOrder: null
  }

  export default {
    name: 'LicenseLists',
    mixins: [SearchMixin],
    components: {
      'license-table': LicenseTable,
      'did-you-know': DidYouKnow,
      'something-went-wrong': SomethingWentWrong,
      'no-license-activity': NoLicenseActivity,
      'loading': Loading,
      'survey-modal': SurveyModal
    },
    data () {
      return {
        isInternal: window.location.host.includes('citrite.net'),
        newPurchases: [],
        expiringLicenses: [],
        hasApiCallFailed: false,
        loading: false,
        isApiCallDone: false,
        newPurchasesTotal: null,
        expiringLicensesTotal: null,
        showMetaframe: this.UrlBuilder.isInternalUser,
        showHardware: false,
        showModal: false,
        modalName: 'survey-license-modal',
        UberAgentDetails: [],
        DeviceTrustDetails: [],
        StrongLicDetails: []
      }
    },
    mounted () {
      const { customerId } = this.$store.getters.person
      this.getLicenses()
      this.getUberAgentLicDetails()
      Promise.all([
        this.bottle.get(this.UrlBuilder.getMetaframeEligibilityUrl(customerId)),
        this.bottle.get(this.UrlBuilder.getHardwareEligibilityUrl(customerId))
      ])
        .then(([{ data: showMetaframe }, { data: showHardware }]) => {
          if (!this.UrlBuilder.isInternalUser) {
            this.showMetaframe = showMetaframe === true || showMetaframe === 'true'
          }
          this.showHardware = showHardware === true || showHardware === 'true'
        })
        .catch((error) => {
          this.loggly.log({
            errorMessage: 'Failed to fetch metaframe and hardware eligibility',
            errorObject: error,
            errorStack: error.stack
          })
        })
    },
    computed: {
      ...mapGetters([
        'isPersonInRole',
        'showSurveyModal'
      ]),
      areBothTablesEmpty () {
        return this.newPurchases.length === 0 && this.expiringLicenses.length === 0
      },
      metaframeUrl () {
        return this.UrlBuilder.metaframeUrl
      },
      hardwareUrl () {
        return this.UrlBuilder.hardwareUrl
      },
      renewLicensesUrl () {
        return this.UrlBuilder.renewLicensesUrl
      },
      upgradeLicensesUrl () {
        return this.UrlBuilder.upgradeLicensesUrl
      }
    },
    methods: {
      ...mapMutations([
        'VALID_SURVEY_DATA',
        'SHOW_SURVEY_MODAL',
        'SURVEY_MODAL_CLOSED'
      ]),
      handleSearchClick () {
        Vue.AppInsights.trackEvent(
          this.search.showAdvancedSearch ? 'Advanced search' : 'Search',
          {
            eventType: 'click',
            searchInput: this.search.searchInput,
            searchType: this.search.findLicenseByOption,
            page: 'Landing page'
          }
        )
        this.$router.push({
          name: 'AllLicenses',
          params: {
            search: this.search,
            isAdvancedSearchVisible: this.search.showAdvancedSearch
          }
        })
      },
      handleAdvancedSearchButtonClick (showAdvancedSearch) {
        this.search.showAdvancedSearch = showAdvancedSearch
      },
      handleQuickFindClick (filterName) {
        this.$router.push({
          name: 'AllLicenses',
          params: {
            quickFind: filterName
          }
        })
      },
      getUberAgentLicDetails () {
        this.loading = true
        this.bottle.get(this.UrlBuilder.getNonStandardLICDetails(this.$store.getters.person.customerId))
          .then(({ data: NonStandardAssetLic }) => {
            this.$store.commit('setUberAgentDetails', NonStandardAssetLic.UberAgentLic)
            this.$store.commit('setDeviceTrustDetails', NonStandardAssetLic.deviceTrustLic)
            this.$store.commit('setStrongLicDetails', NonStandardAssetLic.strongLic)
            this.$store.commit('setUniconLicDetails', NonStandardAssetLic.uniconLic)
          })
          .catch((error) => {
            this.loading = false
            this.hasApiCallFailed = true
            this.isApiCallDone = true
            this.loggly.log({
              errorMessage: 'Failed to get UberAgent asset Details',
              errorObject: error,
              errorStack: error.stack
            })
          })
      },
      getLicenses () {
        this.loading = true
        Promise.all([
          this.bottle.post(this.UrlBuilder.allLicensesUrl, {
            ...requestBody,
            customerId: this.$store.getters.person.customerId,
            filterBy: 'newPurchases'
          }),
          this.bottle.post(this.UrlBuilder.allLicensesUrl, {
            ...requestBody,
            customerId: this.$store.getters.person.customerId,
            filterBy: 'expiringLicenses',
            sortBy: 'ExpirationDate',
            sortByOrder: 'asc'
          })
        ])
          .then(([{ data: newPurchasesData }, { data: expiringLicensesData }]) => {
            this.newPurchases = []
            if (newPurchasesData.Licenses) {
              this.newPurchasesTotal = newPurchasesData.TotalCount
              this.newPurchases = newPurchasesData.Licenses.filter(l => l.RemainingUserCount > 0)
            }
            this.expiringLicenses = expiringLicensesData.Licenses || []
            this.expiringLicensesTotal = expiringLicensesData.TotalCount
            this.loading = false
            this.isApiCallDone = true
          })
          .catch((error) => {
            this.loading = false
            this.hasApiCallFailed = true
            this.isApiCallDone = true
            this.loggly.log({
              errorMessage: 'Failed to fetch licenses in landing page',
              errorObject: error,
              errorStack: error.stack
            })
          })
      },
      handleWebKeyAllocation () {
        this.$router.push({
          name: 'AllLicenses',
          params: {
            showWebkeyModal: true
          }
        })
      },
      allocateManualSurvey () {
        this.SURVEY_MODAL_CLOSED(false)
        this.SHOW_SURVEY_MODAL(true)
        this.VALID_SURVEY_DATA({
          surveyId: this.UrlBuilder.surveyId,
          surveyUrl: this.UrlBuilder.surveyUrl
        })
      }
    }
  }
</script>

<style lang="scss">
  @import '../styles/variables.scss';

  .links-container:first-child {
    margin-top: 30px;
  }

  .links-container a {
    display: block;
  }

  a,
  a:hover {
    text-decoration: none;
    color: $primary;
    margin-bottom: 10px;
  }
  
  /* a,
  a:hover {
    color: inherit;
    text-decoration: underline;
  } */
  .nav-item > a {
    margin-bottom: 0;
    color: $primary;
  }

  .links-container span {
    color: $primary;
    display: none;
  }

  .view-all-licenses-btn {
    padding-left: 50px;
    padding-right: 50px;
    background-color: $primary;
  }

  .blue-link,
  .blue-link:hover {
    color: $primary;
  }

  @media (min-width: 768px) {
    .links-container a {
      display: inline-block;
    }

    .links-container span {
      display: inline-block;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/LicenseLists.vue